/**
 * /auth/callback — OIDC redirect target.
 *
 * Reads ?code + ?state, exchanges them for tokens, validates is_admin,
 * navigates to the returnTo path captured at the start of the flow.
 * On failure, surfaces a recoverable error card with a Try-again button.
 */
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { completeLogin } from '@/auth/auth-client.js';
import { decodeIdToken, getTokens } from '@/auth/token-store.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Status =
  | { kind: 'running' }
  | { kind: 'error'; message: string }
  | { kind: 'done'; returnTo: string };

export default function CallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: 'running' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const returnTo = await completeLogin(window.location.search);
        if (cancelled) return;
        const t = getTokens();
        const claims = decodeIdToken(t?.id_token);
        if (!claims) {
          setStatus({
            kind: 'error',
            message: 'Login succeeded but id_token was missing or unreadable.',
          });
          return;
        }
        if (claims.is_admin !== true) {
          setStatus({
            kind: 'error',
            message:
              'Your account does not have admin privileges. Required role: system_admin or owner.',
          });
          return;
        }
        setStatus({ kind: 'done', returnTo });
      } catch (e) {
        if (!cancelled) setStatus({ kind: 'error', message: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status.kind === 'done') {
      navigate(status.returnTo, { replace: true });
    }
  }, [status, navigate]);

  if (status.kind === 'running' || status.kind === 'done') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {status.kind === 'running' ? 'Completing sign in…' : 'Redirecting…'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in failed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Authentication error</AlertTitle>
            <AlertDescription>{status.message}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/login', { replace: true })}>Try again</Button>
        </CardContent>
      </Card>
    </div>
  );
}
